import { subscriptionCost } from "constants/subscriptions";

import React from "react";

import { KeyboardArrowDown } from "@mui/icons-material";
import { Typography, Box, Button, Grid, Stack, useTheme, alpha, useMediaQuery } from "@mui/material";
import Panel from "components/elements/Panel";
import { Award03, Credit } from "components/icons";
import SubscriptionBadge from "components/modules/SubscriptionBadge";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SubscriptionType } from "types/enums";
import { renderCredits } from "utils/formatter";

import StatusInfoLine from "./StatusInfoLine";
import { BadgeProgress, Card, CardContent, CardTitle } from "./styles";

interface Props {
  badges?: number;
  badgesTotal?: number;
  credits?: number;
  creditsUnclaimed?: number;
}

const Overview: React.FC<Props> = ({ credits, badges, badgesTotal, creditsUnclaimed }) => {
  const { subscriptionsDetails, subscriptionPaused, subscriptionType, creditsBalance } = useUserDataContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { colors } = useMUIThemeModeContext();

  const getSubscriptionCost = (type: SubscriptionType) => {
    return (
      <Stack spacing={0.5} direction="row" alignItems="center" flexWrap={"wrap"}>
        <Stack spacing={0.5} direction="row" alignItems="center">
          <Typography fontWeight={fontWeight.medium} fontSize={{ xs: "1rem", sm: "1.125rem" }}>
            {subscriptionCost[type]}
          </Typography>
          <Credit sx={{ fontSize: 19 }} />
        </Stack>
        <Typography fontWeight={fontWeight.regular} fontSize={{ xs: "0.75rem", sm: "0.875rem" }} color={colors.gray400}>
          {t("creditsPage.overview.monthly")}
        </Typography>
      </Stack>
    );
  };

  const getNextPayment = () => {
    if (subscriptionType !== undefined && creditsBalance !== undefined) {
      if (subscriptionCost[subscriptionType] > creditsBalance) {
        return (
          <Stack>
            <StatusInfoLine
              i18nKey="creditsPage.overview.nextPayment"
              date={subscriptionsDetails?.nextSubscriptionRenewalDate}
            />
            <Typography
              fontWeight={fontWeight.medium}
              fontSize={{ xs: "0.75rem", sm: "0.875rem" }}
              color={colors.error}>
              {t("creditsPage.overview.notEnoughCredits")}
            </Typography>
          </Stack>
        );
      }
    }

    if (subscriptionPaused) {
      return (
        <Typography fontWeight={fontWeight.medium} fontSize={{ xs: "0.75rem", sm: "0.875rem" }} color={colors.error}>
          {t("creditsPage.overview.subscriptionPaused")}
        </Typography>
      );
    }

    return (
      <Stack>
        <StatusInfoLine i18nKey="creditsPage.overview.nextPayment" date={subscriptionsDetails?.nextSubscriptionRenewalDate} />
        {subscriptionsDetails?.nextCreditsRechargeDate ? (
          <StatusInfoLine i18nKey="creditsPage.overview.nextCreditsRecharge" date={subscriptionsDetails?.nextCreditsRechargeDate} />
        ) : (
          <StatusInfoLine i18nKey="creditsPage.overview.enoughCreditsBy" date={subscriptionsDetails?.enoughCreditsUntilDate} />
        )}
      </Stack>
    );
  };

  return (
    <Panel shadow={false} sx={{ width: "100%", p: { xs: 2, md: 3 }, mb: 4 }}>
      <Grid container spacing={{ xs: 1.5, md: 3 }} alignItems={"stretch"}>
        <Grid item xs={12} lg={4} height="auto">
          <Card>
            <CardTitle>{t("creditsPage.overview.accountState")}</CardTitle>
            <CardContent>
              <Stack spacing={0.5} direction="row" alignItems="center">
                <Credit sx={{ fontSize: 24 }} />
                <Box pt={0.5}>
                  <Typography fontWeight={fontWeight.medium} fontSize={{ xs: "1.125rem", sm: "1.5rem" }}>
                    {renderCredits(credits)}
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                component={Link}
                to="#mesicni-platba"
                endIcon={<KeyboardArrowDown />}
                size={isMobile ? "small" : "medium"}>
                {t("creditsPage.overview.fillUpCredits")}
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4} height="auto">
          <Card>
            <CardTitle>{t("creditsPage.overview.activeSubscription")}</CardTitle>
            <CardContent>
              <Stack spacing={2} direction="row" alignItems="center">
                <SubscriptionBadge disable size={isMobile ? "small" : "normal"} />
                {subscriptionType && subscriptionType === SubscriptionType.FREE && (
                  <Typography fontWeight={fontWeight.medium} fontSize={{ xs: "1rem", sm: "1.125rem" }}>
                    {t("creditsPage.overview.accountFree")}
                  </Typography>
                )}
                {subscriptionType && subscriptionType !== SubscriptionType.FREE && getSubscriptionCost(subscriptionType)}
              </Stack>
              {subscriptionType && subscriptionType === SubscriptionType.FREE && (
                <Button
                  color="success"
                  variant="contained"
                  component={Link}
                  to="#predplatne"
                  endIcon={<KeyboardArrowDown />}
                  size={isMobile ? "small" : "medium"}>
                  {t("creditsPage.overview.selectSubscription")}
                </Button>
              )}
              {subscriptionType && subscriptionType !== SubscriptionType.FREE && getNextPayment()}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4} height="auto">
          <Card>
            <CardTitle>{t("myBadges")}</CardTitle>
            <CardContent>
              <Stack spacing={1} direction="row" alignItems="center">
                <Award03 sx={{ fontSize: isMobile ? 24 : 36, color: theme.palette.tertiary.main }} />
                <Typography fontWeight={fontWeight.medium} fontSize={{ xs: "1rem", sm: "1.125rem" }}>
                  {t("youHaveCollected")} {badges} {t("count.of")} {badgesTotal}{" "}
                  {t("badge.collected", { count: badgesTotal }).toLowerCase()}
                </Typography>
              </Stack>
              {Boolean(creditsUnclaimed) && (
                <Button
                  variant="outlined"
                  component={Link}
                  to="#odznaky"
                  size={isMobile ? "small" : "medium"}
                  endIcon={<KeyboardArrowDown />}
                  sx={{
                    color: theme.palette.tertiary.main,
                    borderColor: theme.palette.tertiary.main,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.tertiary.main, 0.1),
                      borderColor: theme.palette.tertiary.main,
                    },
                  }}>
                  {renderCredits(creditsUnclaimed)} {t("toClaim")}
                </Button>
              )}
              {creditsUnclaimed === 0 && <BadgeProgress variant="determinate" value={badgesTotal && badges ? (badges / badgesTotal) * 100 : 0} />}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Panel>
  );
};

export default Overview;
